import type { FeedFilePending } from '$lib/models/ccr/types';

export interface FileErrorDetail {
  reason: string;
  fix: string;
}

// Feed providers' SFTP accounts only have Create + List access to the ingestions folder (no Write/Delete),
// so a corrected re-upload always lands as a brand-new file — it never overwrites or clears the bad one.
// Deleting it first (rather than after) avoids it sitting around to be picked up by the next scheduled
// processing pass, and avoids two similarly-named files in the table once the correction lands.
export const DELETE_FIRST_NOTE =
  "Delete this file from the table above first — the provider's FTP access can only add new files, it can't replace or remove this one, so it will keep showing as an error until it's removed here.";

// Mirrors PgpDecryptionErrorReason in the CCR backend (Alii.CreditCardRec.Domain.Features.Security).
const DECRYPTION_ERROR_DETAILS: Record<string, FileErrorDetail> = {
  NotPgpEncrypted: {
    reason: 'File is not a valid PGP-encrypted file',
    fix: `${DELETE_FIRST_NOTE} Then confirm the corrected file is exported in PGP format — opening it in a text editor should show a header like '-----BEGIN PGP MESSAGE-----' at the top — and have the provider upload it.`,
  },
  KeyMismatch: {
    reason: 'File was encrypted with a different key',
    fix: `${DELETE_FIRST_NOTE} Then have the provider re-export the file using Alii's PGP public key and upload the corrected version.`,
  },
  Truncated: {
    reason: 'File is incomplete',
    fix: `${DELETE_FIRST_NOTE} Then ask the provider to resend the file in full — this one's upload or transfer was cut short and is missing its closing '-----END PGP MESSAGE-----' marker.`,
  },
};

const UNKNOWN_DECRYPTION_ERROR: FileErrorDetail = {
  reason: 'File could not be decrypted',
  fix: `${DELETE_FIRST_NOTE} Then have the provider re-export the file using the correct PGP key and upload it again.`,
};

/**
 * Returns the specific reason(s) a pending feed file is flagged as an error, each paired
 * with guidance on how to fix it. Mirrors the precedence used by the backend when populating
 * FeedFilePendingDto: a decryption failure means account extraction never ran, so it's reported alone.
 */
export function getFileErrorReasons(file: Partial<FeedFilePending>): FileErrorDetail[] {
  if (file.canBeDecrypted === false) {
    const decryptionErrorReason = file.decryptionErrorReason ? DECRYPTION_ERROR_DETAILS[file.decryptionErrorReason] : undefined;
    return [decryptionErrorReason ?? UNKNOWN_DECRYPTION_ERROR];
  }

  const reasons: FileErrorDetail[] = [];

  if (file.extractErrorMessage) {
    // CCR's own parser message, shown verbatim — it already names the offending field, and for Visa the line number.
    reasons.push({
      reason: file.extractErrorMessage,
      fix: `${DELETE_FIRST_NOTE} Then send this error to the feed provider, have them correct the file, and upload the corrected version.`,
    });
  }

  if (file.isAccountInformationMissing) {
    // Shown alongside the raw parser message (when present) so the friendly explanation isn't lost.
    reasons.push({
      reason: 'Missing account information',
      fix: `${DELETE_FIRST_NOTE} Then confirm the corrected file contains account data in the expected CCR format before the provider uploads it.`,
    });
  }

  if (file.accountsFound && file.accountsFound.length > 0 && !file.accountsFound.every((account) => account.isMasked === true)) {
    reasons.push({
      reason: 'Unmasked account number found',
      fix: `${DELETE_FIRST_NOTE} Then have the provider re-export the file with account numbers masked — e.g. "547233******8028" — instead of the full number, e.g. "3748211111114883" — and upload the corrected version.`,
    });
  }

  return reasons;
}
